import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, Package, MapPin, Calendar, ChevronLeft, ChevronRight, Search, X, CalendarDays } from "lucide-react";
import { JobStatusBadge } from "@/components/JobStatusBadge";
import { UKRegPlate } from "@/components/UKRegPlate";
import { format, startOfDay, endOfDay, subDays } from "date-fns";
import { DesktopJobsTable } from "@/components/desktop/DesktopJobsTable";
import { useIsMobile } from "@/hooks/use-mobile";
import { isNative } from "@/lib/capacitor";
import { useUserRole } from "@/hooks/useUserRole";
import { useCompany } from "@/hooks/useCompany";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Job {
  id: string;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_registration: string;
  delivery_address: string;
  collection_address: string | null;
  status: string;
  completed_at: string | null;
  delivery_completed_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  is_return_leg: boolean;
  parent_job_id: string | null;
  has_return_vehicle: boolean;
  customers: {
    name: string;
  } | null;
  returnLeg?: Job;
}

type DateRange = "today" | "7days" | "30days" | "custom";

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  today: "Today",
  "7days": "Last 7 days",
  "30days": "Last 30 days",
  custom: "Custom range",
};

function getDateBounds(range: DateRange, customFrom?: Date, customTo?: Date): { from: string; to: string } {
  const now = new Date();
  if (range === "custom" && customFrom && customTo) {
    return { from: startOfDay(customFrom).toISOString(), to: endOfDay(customTo).toISOString() };
  }
  const daysBack = range === "30days" ? 30 : range === "7days" ? 7 : 0;
  return {
    from: startOfDay(subDays(now, daysBack)).toISOString(),
    to: endOfDay(now).toISOString(),
  };
}

export const CompletedJobsSection = () => {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filter, setFilter] = useState<"delivered" | "cancelled">("delivered");
  const [dateRange, setDateRange] = useState<DateRange>("today");
  const [customFrom, setCustomFrom] = useState<Date | undefined>(undefined);
  const [customTo, setCustomTo] = useState<Date | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const isMobile = useIsMobile();
  const { isAdmin } = useUserRole();
  const { companyId } = useCompany();
  const fetchInFlightRef = useRef(false);
  const realtimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const safeFormatDate = (value: string | null | undefined, pattern: string, fallback = "—") => {
    if (!value) return fallback;
    try {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return fallback;
      return format(parsed, pattern);
    } catch {
      return fallback;
    }
  };

  const fetchJobs = useCallback(async () => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    try {
      const { from, to } = getDateBounds(dateRange, customFrom, customTo);

      const { data, error } = await supabase
        .from("jobs")
        .select(`
          id,
          vehicle_make,
          vehicle_model,
          vehicle_registration,
          delivery_address,
          delivery_postcode,
          collection_address,
          collection_postcode,
          driver_id,
          estimated_cost,
          invoice_amount,
          status,
          completed_at,
          delivery_completed_at,
          cancelled_at,
          cancel_reason,
          is_return_leg,
          parent_job_id,
          has_return_vehicle,
          is_draft,
          execution_type,
          executing_company_id,
          vin_number,
          customers (name),
          drivers (name)
        `)
        .or(`and(delivery_completed_at.gte.${from},delivery_completed_at.lte.${to}),and(cancelled_at.gte.${from},cancelled_at.lte.${to})`)
        .order("completed_at", { ascending: false })
        .limit(500);

      if (error) throw error;

      // Merge return legs under their parent outbound job
      const allJobs = (data || []) as Job[];
      const returnLegMap = new Map<string, Job>();
      const outboundJobs: Job[] = [];

      for (const j of allJobs) {
        if (j.is_return_leg && j.parent_job_id) {
          returnLegMap.set(j.parent_job_id, j);
        } else {
          outboundJobs.push(j);
        }
      }

      for (const j of outboundJobs) {
        const returnLeg = returnLegMap.get(j.id);
        if (returnLeg) {
          j.returnLeg = returnLeg;
          returnLegMap.delete(j.id);
        }
      }

      for (const orphan of returnLegMap.values()) {
        outboundJobs.push(orphan);
      }

      setJobs(outboundJobs);
    } catch (error) {
      console.error("Error fetching completed jobs:", error);
    } finally {
      setLoading(false);
      fetchInFlightRef.current = false;
    }
  }, [dateRange, customFrom, customTo]);

  // ── Initial load + realtime subscription ──
  useEffect(() => {
    setLoading(true);
    fetchJobs();

    if (!companyId) return;

    const debouncedRefetch = () => {
      if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
      realtimeDebounceRef.current = setTimeout(() => fetchJobs(), 500);
    };

    const channel = supabase
      .channel('completed-jobs-history')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs', filter: `company_id=eq.${companyId}` }, debouncedRefetch)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs', filter: `executing_company_id=eq.${companyId}` }, debouncedRefetch)
      .subscribe();

    return () => {
      if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
      supabase.removeChannel(channel);
    };
  }, [companyId, fetchJobs]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, filter, dateRange, searchQuery, customFrom, customTo]);

  // Split and search
  const deliveredJobs = useMemo(() => jobs.filter(j => !j.cancelled_at && j.delivery_completed_at), [jobs]);
  const cancelledJobs = useMemo(() => jobs.filter(j => !!j.cancelled_at), [jobs]);

  const applySearch = useCallback((list: Job[]) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter(j => {
      const reg = j.vehicle_registration?.toLowerCase() || "";
      const customer = j.customers?.name?.toLowerCase() || "";
      const delivery = j.delivery_address?.toLowerCase() || "";
      const collection = j.collection_address?.toLowerCase() || "";
      const make = j.vehicle_make?.toLowerCase() || "";
      const model = j.vehicle_model?.toLowerCase() || "";
      const id = j.id?.toLowerCase() || "";
      return reg.includes(q) || customer.includes(q) || delivery.includes(q) || collection.includes(q) || make.includes(q) || model.includes(q) || id.includes(q);
    });
  }, [searchQuery]);

  const filteredDelivered = useMemo(() => applySearch(deliveredJobs), [applySearch, deliveredJobs]);
  const filteredCancelled = useMemo(() => applySearch(cancelledJobs), [applySearch, cancelledJobs]);
  const filteredJobs = filter === "delivered" ? filteredDelivered : filteredCancelled;

  const totalJobs = filteredJobs.length;
  const totalPages = Math.ceil(totalJobs / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentJobs = filteredJobs.slice(startIndex, endIndex);

  const dateRangeLabel = dateRange === "custom" && customFrom && customTo
    ? `${format(customFrom, "dd MMM")} – ${format(customTo, "dd MMM yyyy")}`
    : DATE_RANGE_LABELS[dateRange];

  const sectionTitle = filter === "delivered"
    ? dateRange === "today" ? "Delivered Today" : "Delivered Jobs"
    : dateRange === "today" ? "Cancelled Today" : "Cancelled Jobs";

  const sectionDescription = filter === "delivered"
    ? `Delivered jobs — ${dateRangeLabel}`
    : `Cancelled jobs — ${dateRangeLabel}`;

  if (loading) {
    return (
      <Card className="border-border/50 bg-card/80 backdrop-blur mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" />
            Completed Jobs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-muted-foreground py-4">
            <Package className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading jobs…</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card ref={sectionRef} className="border-border/50 bg-card/80 backdrop-blur mb-4 sm:mb-6 md:mb-8">
      <CardHeader className="p-3 sm:p-4 md:p-6">
        <div className="flex flex-col gap-3">
          {/* Row 1: Title + Delivered/Cancelled toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl">
                {filter === "delivered" ? (
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-destructive" />
                )}
                {sectionTitle}
                <Badge variant="secondary" className={`text-xs ${filter === "delivered" ? "bg-green-500/10 text-green-700" : "bg-destructive/10 text-destructive"}`}>
                  {totalJobs}
                </Badge>
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm mt-1">
                {sectionDescription}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex rounded-lg border bg-muted/50 p-0.5">
                <Button
                  variant={filter === "delivered" ? "default" : "ghost"}
                  size="sm"
                  className="h-7 text-xs px-3 rounded-md"
                  onClick={() => setFilter("delivered")}
                >
                  Delivered ({filteredDelivered.length})
                </Button>
                <Button
                  variant={filter === "cancelled" ? "default" : "ghost"}
                  size="sm"
                  className="h-7 text-xs px-3 rounded-md"
                  onClick={() => setFilter("cancelled")}
                >
                  Cancelled ({filteredCancelled.length})
                </Button>
              </div>
              <Button variant="outline" size="sm" className="text-xs sm:text-sm" onClick={() => navigate("/previous-jobs")}>
                View All
              </Button>
            </div>
          </div>

          {/* Row 2: Date range + Search */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            {/* Date range selector */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {(["today", "7days", "30days"] as DateRange[]).map((r) => (
                <Button
                  key={r}
                  variant={dateRange === r ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs px-2.5"
                  onClick={() => { setDateRange(r); setCustomFrom(undefined); setCustomTo(undefined); }}
                >
                  {DATE_RANGE_LABELS[r]}
                </Button>
              ))}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={dateRange === "custom" ? "default" : "outline"}
                    size="sm"
                    className="h-7 text-xs px-2.5 gap-1"
                  >
                    <CalendarDays className="w-3 h-3" />
                    {dateRange === "custom" && customFrom && customTo
                      ? `${format(customFrom, "dd/MM")} – ${format(customTo, "dd/MM")}`
                      : "Custom"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarPicker
                    mode="range"
                    className="p-3 pointer-events-auto"
                    selected={customFrom && customTo ? { from: customFrom, to: customTo } : undefined}
                    onSelect={(range) => {
                      if (range?.from) setCustomFrom(range.from);
                      if (range?.to) {
                        setCustomTo(range.to);
                        setDateRange("custom");
                      }
                    }}
                    disabled={(date) => date > new Date()}
                    numberOfMonths={isMobile ? 1 : 2}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Search input */}
            <div className="relative flex-1 sm:max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Search reg, customer, address…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-7 text-xs pl-8 pr-7"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
        {filteredJobs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchQuery ? (
              <>
                <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-lg font-medium mb-1">No results found</p>
                <p className="text-sm">Try a different search term or adjust your date range</p>
              </>
            ) : filter === "delivered" ? (
              <>
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50 text-green-600" />
                <p className="text-lg font-medium mb-1">No deliveries in this period</p>
                <p className="text-sm">Completed deliveries will appear here</p>
              </>
            ) : (
              <>
                <XCircle className="w-12 h-12 mx-auto mb-3 opacity-50 text-destructive" />
                <p className="text-lg font-medium mb-1">No cancellations in this period</p>
                <p className="text-sm">Cancelled jobs will appear here</p>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            {!isMobile && !isNative && isAdmin ? (
              <DesktopJobsTable
                jobs={currentJobs}
                drivers={[]}
                isAdmin={isAdmin}
                variant="completed"
              />
            ) : (
              <div className="space-y-3">
                {currentJobs.map((job) => (
                  <div
                    key={job.id}
                    onClick={() => navigate(`/job/${job.id}`)}
                    className="flex flex-col gap-1 p-3 sm:p-4 rounded-lg border bg-background/50 hover:bg-background transition-colors cursor-pointer group"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                      <div className="flex items-start sm:items-center gap-3 flex-1">
                        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          job.cancelled_at ? "bg-destructive/10" : "bg-green-500/10"
                        }`}>
                          {job.cancelled_at ? (
                            <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-destructive" />
                          ) : (
                            <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="font-semibold text-sm sm:text-base truncate">
                              {job.vehicle_make} {job.vehicle_model}
                            </p>
                            <UKRegPlate registration={job.vehicle_registration} />
                            {job.returnLeg && (
                              <Badge variant="secondary" className="text-xs">+ Return</Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
                            {job.customers && (
                              <span className="flex items-center gap-1">
                                <Package className="w-3 h-3" />
                                <span className="truncate max-w-[100px] sm:max-w-none">{job.customers.name}</span>
                              </span>
                            )}
                            <span className="flex items-center gap-1 truncate hidden sm:flex">
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{job.delivery_address}</span>
                            </span>
                          </div>
                          {job.returnLeg && (
                            <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1.5">
                              <span className="font-medium">↩ Return:</span>
                              <UKRegPlate registration={job.returnLeg.vehicle_registration} size="sm" />
                              <span>•</span>
                              <span className="truncate">{job.returnLeg.delivery_address}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3 sm:ml-4 mt-2 sm:mt-0">
                        <div className="text-left sm:text-right text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {job.cancelled_at
                              ? safeFormatDate(job.cancelled_at, dateRange === "today" ? "h:mm a" : "dd MMM, h:mm a")
                              : safeFormatDate(job.delivery_completed_at, dateRange === "today" ? "h:mm a" : "dd MMM, h:mm a")}
                          </div>
                        </div>
                        <JobStatusBadge
                          status={job.cancelled_at ? "cancelled" : "completed"}
                          deliveryCompleted={!!job.delivery_completed_at}
                          cancelledAt={job.cancelled_at}
                          cancelReason={job.cancel_reason}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {totalJobs > itemsPerPage && (
              <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-t pt-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm text-muted-foreground">Per page:</span>
                  <Select value={itemsPerPage.toString()} onValueChange={(val) => setItemsPerPage(Number(val))}>
                    <SelectTrigger className="w-16 h-8 text-xs sm:text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto justify-between sm:justify-end">
                  <span className="text-xs sm:text-sm text-muted-foreground">
                    {startIndex + 1}-{Math.min(endIndex, totalJobs)} of {totalJobs}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setCurrentPage(currentPage - 1); sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                      disabled={currentPage === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setCurrentPage(currentPage + 1); sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                      disabled={currentPage === totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
